/**
 * Kit de soporte v1 — ReportarProblemaButton, la entrada global "Reportar un
 * problema" (ADR-0025), con el mismo ícono (`Bug`), etiqueta y lugar que en
 * analyzer y proveedores.
 *
 * Es dueño del borrador y del `report_id`: cerrar el diálogo conserva el
 * texto, y un reintento tras un envío fallido usa el mismo id. Uno nuevo se
 * saca solo después de un envío exitoso.
 *
 * `trigger` deja usar el botón de ícono de la barra de la app (p. ej. sobre una
 * barra oscura) sin cambiar el diálogo.
 *
 * Funciona dentro o fuera de un Router: fuera (p. ej. en un ErrorBoundary que
 * envuelve al BrowserRouter) toma la ruta de `window.location`.
 */

import { useState, type ReactNode } from "react";
import { useInRouterContext, useLocation } from "react-router-dom";
import { Bug } from "lucide-react";

import { entidadDeRuta, soporteConfig, type SoporteConfig } from "./config";
import { ReportarProblemaDialog } from "./ReportarProblemaDialog";
import { emptyDraft, type ReportDraft } from "./reportDraft";
import { newReportId } from "./supportContext";

export interface ReportarProblemaButtonProps {
  /** "icon" para la barra superior; "inline" para la pantalla de error. */
  variant?: "icon" | "inline";
  label?: string;
  /** Otra configuración (p. ej. otro canal); por defecto la de `configurarSoporte`. */
  config?: SoporteConfig;
  trigger?: (open: () => void, label: string) => ReactNode;
}

export function ReportarProblemaButton(props: ReportarProblemaButtonProps) {
  // Dos componentes en lugar de un hook condicional: estar o no dentro de un
  // Router no cambia durante la vida del botón.
  return useInRouterContext() ? (
    <BotonEnRouter {...props} />
  ) : (
    <Boton {...props} pathname={window.location.pathname} />
  );
}

function BotonEnRouter(props: ReportarProblemaButtonProps) {
  const { pathname } = useLocation();
  return <Boton {...props} pathname={pathname} />;
}

function Boton({
  variant = "icon",
  label = "Reportar un problema",
  config,
  trigger,
  pathname,
}: ReportarProblemaButtonProps & { pathname: string }) {
  const cfg = config ?? soporteConfig();
  const [open, setOpen] = useState(false);
  const [reportId, setReportId] = useState(newReportId);
  const [draft, setDraft] = useState<ReportDraft>(() =>
    emptyDraft(entidadDeRuta(pathname, cfg)),
  );
  const [sentFolio, setSentFolio] = useState<string | null>(null);

  const close = () => {
    setOpen(false);
    setSentFolio(null);
  };

  const openDialog = () => {
    // Un borrador sin tocar sigue a la pantalla: la entidad de la página que se reporta.
    if (!draft.descripcion && !draft.captura) {
      setDraft(emptyDraft(entidadDeRuta(pathname, cfg)));
    }
    setOpen(true);
  };

  let entry: ReactNode;
  if (trigger) {
    entry = trigger(openDialog, label);
  } else if (variant === "icon") {
    entry = (
      <button
        type="button"
        onClick={openDialog}
        aria-label={label}
        title={label}
        className="grid h-8 w-8 place-items-center rounded text-fg-2 transition-colors hover:bg-card-2 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1"
      >
        <Bug aria-hidden className="h-4 w-4" />
      </button>
    );
  } else {
    entry = (
      <button
        type="button"
        onClick={openDialog}
        className="rounded border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-card-2"
      >
        {label}
      </button>
    );
  }

  return (
    <>
      {entry}
      {open ? (
        <ReportarProblemaDialog
          config={cfg}
          draft={draft}
          onDraftChange={setDraft}
          reportId={reportId}
          pathname={pathname}
          sentFolio={sentFolio}
          onClose={close}
          onSent={(folio) => {
            setSentFolio(folio);
            setReportId(newReportId());
            setDraft(emptyDraft(entidadDeRuta(pathname, cfg)));
          }}
        />
      ) : null}
    </>
  );
}
